import { type ReactNode, useId } from 'react'
import { type MessageDescriptor, useIntl } from 'react-intl'

import { type SupportLine, type SupportStatus } from '@onr/schema'
import { ExternalLinkIcon } from 'lucide-react'

import { InlineError } from '@/components/atoms/InlineError'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select/Select.tsx'
import { cn } from '@/lib/utils.ts'

const STATUSES = [
  'toHandle',
  'waitingRequester',
  'waitingValian',
  'settled',
] as const satisfies ReadonlyArray<SupportStatus>

const UNASSIGNED = 'none'

export interface SupportAgentOption {
  readonly piloteUserId: number
  readonly label: string
}

export interface SupportAgentDetailsProps {
  readonly line: SupportLine
  readonly status: SupportStatus
  readonly assigneeId: number | null
  readonly agents: ReadonlyArray<SupportAgentOption>
  readonly requester: {
    readonly name: string
    readonly permissions: string
    readonly central: string
    readonly email: string | null
  }
  readonly context: {
    readonly page: string
    readonly object: { readonly label: string; readonly url: string | null } | null
    readonly system: string
  }
  readonly linearTicket?: {
    readonly identifier: string
    readonly url: string
    readonly title: string
    readonly status?: { readonly name: string; readonly type: string } | null
  } | null
  readonly saving: boolean
  readonly error: boolean
  readonly onStatusChange: (status: SupportStatus) => void
  readonly onAssigneeChange: (piloteUserId: number | null) => void
}

const DetailRows = ({ rows }: { rows: ReadonlyArray<readonly [string, ReactNode]> }) => (
  <dl className="grid grid-cols-[80px_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-sm">
    {rows.map(([label, value]) => (
      <div key={label} className="contents">
        <dt className="text-gray-500">{label}</dt>
        <dd className="break-words text-gray-900">{value}</dd>
      </div>
    ))}
  </dl>
)

const LINEAR_STATUS_TINTS: Readonly<Record<string, string>> = {
  started: 'border-blue-300 bg-blue-100 text-blue-800',
  completed: 'border-green-300 bg-green-100 text-green-800',
}

const LinearStatusTag = ({ status }: { status: { readonly name: string; readonly type: string } }) => (
  <span
    className={cn(
      'max-w-full rounded-[3px] border px-2 py-[3px] text-sm leading-none [overflow-wrap:anywhere]',
      LINEAR_STATUS_TINTS[status.type] ?? 'border-gray-300 bg-gray-100 text-gray-700'
    )}>
    {status.name}
  </span>
)

export const SupportAgentDetails = ({
  line,
  status,
  assigneeId,
  agents,
  requester,
  context,
  linearTicket,
  saving,
  error,
  onStatusChange,
  onAssigneeChange,
}: SupportAgentDetailsProps) => {
  const intl = useIntl()
  const titleId = useId()
  const statusId = useId()
  const escouadeId = useId()
  const linearId = useId()
  const label = (id: NonNullable<MessageDescriptor['id']>) => intl.formatMessage({ id })

  return (
    <aside
      aria-labelledby={titleId}
      className="flex h-full w-[300px] max-w-full shrink-0 flex-col gap-[18px] overflow-y-auto rounded-[20px] bg-white px-5 py-4">
      <h2 id={titleId} className="font-heading text-lg font-semibold text-gray-900">
        {label('soutien.details.title')}
      </h2>

      {line === 'l2' ? (
        <dl className="grid grid-cols-[80px_minmax(0,1fr)] items-center gap-x-2 gap-y-2.5 text-base">
          <dt className="font-bold text-gray-800">{label('soutien.details.status')}</dt>
          <dd>
            <span className="inline-block rounded-[3px] border border-gray-600 bg-gray-100 px-2 pt-1 pb-[3px] text-sm leading-none text-gray-800">
              {label(`soutien.status.${status}`)}
            </span>
          </dd>
          <dt className="font-bold text-gray-800">{label('soutien.details.escouade')}</dt>
          <dd className="text-gray-900">
            {agents.find(({ piloteUserId }) => piloteUserId === assigneeId)?.label ??
              label('soutien.details.escouade.none')}
          </dd>
        </dl>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <label id={statusId} className="font-bold text-gray-800">
              {label('soutien.details.status')}
            </label>
            <Select value={status} disabled={saving} onValueChange={(value) => onStatusChange(value as SupportStatus)}>
              <SelectTrigger
                aria-labelledby={statusId}
                variant="field"
                className="h-9 border-blue-300 bg-blue-100 px-2.5 text-blue-900 hover:border-blue-400">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {label(`soutien.status.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label id={escouadeId} className="font-bold text-gray-800">
              {label('soutien.details.escouade')}
            </label>
            <Select
              value={assigneeId === null ? UNASSIGNED : String(assigneeId)}
              disabled={saving}
              onValueChange={(value) => onAssigneeChange(value === UNASSIGNED ? null : Number(value))}>
              <SelectTrigger aria-labelledby={escouadeId} variant="field" className="h-9 px-2.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.piloteUserId} value={String(agent.piloteUserId)}>
                    {agent.label}
                  </SelectItem>
                ))}
                <SelectItem value={UNASSIGNED}>{label('soutien.details.escouade.none')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {error && <InlineError message={label('soutien.details.error')} />}

      {linearTicket && (
        <section aria-labelledby={linearId} className="flex flex-col gap-2 border-t border-gray-200 pt-4">
          <h3 id={linearId} className="text-base font-bold text-gray-800">
            {label('soutien.details.linear')}
          </h3>
          <a
            href={linearTicket.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-1 rounded-[4px] border border-gray-300 px-3 py-2.5 hover:border-gray-400">
            <span className="flex flex-wrap items-center justify-between gap-1.5">
              <span className="inline-flex items-center gap-1 text-sm font-bold text-blue-700">
                {linearTicket.identifier}
                <ExternalLinkIcon aria-label={label('soutien.details.linear.newTab')} className="size-3" />
              </span>
              {linearTicket.status && <LinearStatusTag status={linearTicket.status} />}
            </span>
            <span className="text-base leading-[1.35] [overflow-wrap:anywhere] text-gray-900">
              {linearTicket.title}
            </span>
          </a>
        </section>
      )}

      <section className="flex flex-col gap-2 border-t border-gray-200 pt-4">
        <h3 className="text-base font-bold text-gray-800">{label('soutien.details.requester')}</h3>
        <DetailRows
          rows={[
            [label('soutien.details.requester.name'), requester.name],
            [label('soutien.details.requester.permissions'), requester.permissions],
            [label('soutien.details.requester.central'), requester.central],
            [label('soutien.details.requester.email'), requester.email ?? label('soutien.details.requester.noEmail')],
          ]}
        />
      </section>

      <section className="flex flex-col gap-2 border-t border-gray-200 pt-4">
        <h3 className="text-base font-bold text-gray-800">{label('soutien.details.context')}</h3>
        <DetailRows
          rows={[
            [label('soutien.context.page'), context.page],
            ...(context.object
              ? [
                  [
                    label('soutien.details.context.object'),
                    context.object.url ? (
                      <a
                        key="object"
                        href={context.object.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:text-blue-600">
                        {context.object.label} ↗
                      </a>
                    ) : (
                      context.object.label
                    ),
                  ] as const,
                ]
              : []),
            [label('soutien.context.system'), context.system],
          ]}
        />
      </section>
    </aside>
  )
}
